import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Eye, FileText, Paperclip, Pencil, Plus, ShieldAlert, Trash2 } from 'lucide-react';
import { CURRENCIES } from '@kmdx/contracts';
import { cn } from '@/lib/cn';
import { formatDate } from '@/lib/format';
import { useVehicles } from '@/api/hooks/catalog';
import { useDocumentsOf } from '@/api/hooks/documents';
import { useTrips } from '@/api/hooks/trips';
import { uploadDocument } from '@/api/hooks/uploads';
import {
  AT_FAULT,
  AT_FAULT_BG,
  INCIDENT_KINDS,
  INCIDENT_KIND_BG,
  useDeleteIncident,
  useSaveIncident,
  type AtFault,
  type DossierEvent,
  type Incident,
  type IncidentKind,
} from '@/api/hooks/dossier';
import { Badge, type Tone } from '@/components/ui/Badge';
import { Button, IconButton } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Dialog } from '@/components/ui/Dialog';
import { Checkbox, DatePicker, Field, Input, Select, Textarea } from '@/components/ui/Field';
import { toast } from '@/stores/toast';

const KIND_TONE: Record<IncidentKind, Tone> = {
  accident: 'danger',
  vehicle_damage: 'warn',
  cargo_damage: 'warn',
  fine: 'info',
  complaint: 'neutral',
  other: 'neutral',
};

const SOURCE_BG: Record<DossierEvent['source'], string> = {
  stop_damage: 'щета по товара',
  trip_incident: 'от курса',
  fine_expense: 'глоба — разход',
  infringement: 'нарушение',
};

const money = (amount: number, ccy: string | null) =>
  `${amount.toLocaleString('bg-BG', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ${ccy === 'EUR' || !ccy ? '€' : ccy}`;

/**
 * ДОСИЕТО: произшествия, щети, глоби, оплаквания — вписани от офиса, плюс
 * каквото системата вече знае от курсовете. Клиентът: „за да имаме представа
 * дали е имал катастрофи".
 */
export function IncidentsCard({
  driverId,
  incidents,
  events,
  canEdit,
}: {
  driverId: string;
  incidents: Incident[];
  events: DossierEvent[];
  canEdit: boolean;
}) {
  const [editing, setEditing] = useState<Incident | 'new' | null>(null);
  const remove = useDeleteIncident();
  /** Прикачените снимки и протоколи — документи на шофьора (не личните). */
  const attachments = useDocumentsOf({ driverId, types: 'photo_damage,other' });
  const fileById = new Map((attachments.data?.items ?? []).map((d) => [d.id, d]));

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-1.5">
            <ShieldAlert className="size-4 text-ink-dim" />
            Досие — произшествия, щети, глоби
          </span>
        }
        subtitle="Вписват се, когато се случат — и онези отпреди назначаването, по негови думи."
        actions={
          canEdit ? (
            <Button size="sm" variant="secondary" icon={<Plus className="size-3.5" />} onClick={() => setEditing('new')}>
              Впиши
            </Button>
          ) : null
        }
      />
      <CardBody className="space-y-4">
        {incidents.length === 0 ? (
          <p className="text-sm text-ink-dim">Няма вписани произшествия.</p>
        ) : (
          <ul className="divide-y divide-line">
            {incidents.map((i) => (
              <li key={i.id} className="py-2.5 first:pt-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-xs text-ink-dim">{formatDate(i.occurredOn)}</span>
                  <Badge tone={KIND_TONE[i.kind]}>{INCIDENT_KIND_BG[i.kind]}</Badge>
                  <span className={cn('text-xs', i.atFault === 'yes' || i.atFault === 'partly' ? 'font-medium text-danger' : 'text-ink-dim')}>
                    {AT_FAULT_BG[i.atFault]}
                  </span>
                  {i.beforeEmployment ? <Badge tone="neutral">преди да постъпи</Badge> : null}
                  {i.cost !== null ? <span className="text-xs font-medium">{money(i.cost, i.ccy)}</span> : null}
                  {canEdit ? (
                    <span className="ml-auto flex items-center gap-0.5">
                      <IconButton label="Поправи" onClick={() => setEditing(i)}>
                        <Pencil className="size-3.5" />
                      </IconButton>
                      <IconButton
                        label="Изтрий"
                        onClick={() => window.confirm('Да изтрия ли този запис от досието?') && remove.mutate(i.id)}
                      >
                        <Trash2 className="size-3.5" />
                      </IconButton>
                    </span>
                  ) : null}
                </div>
                <p className="mt-1 text-sm">{i.description}</p>
                <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-ink-dim">
                  {i.place ? <span>{i.place}</span> : null}
                  {i.vehicle ? <span>бус {i.vehicle}</span> : null}
                  {i.tripId && i.tripNumber ? (
                    <Link to="/trips/$tripId" params={{ tripId: i.tripId }} className="hover:text-accent">
                      курс {i.tripNumber}
                    </Link>
                  ) : null}
                  {i.reference ? <span>№ {i.reference}</span> : null}
                  {i.createdByName ? <span>вписа {i.createdByName}</span> : null}
                </div>
                {i.notes ? <p className="mt-0.5 text-xs text-ink-faint">{i.notes}</p> : null}
                {i.documentIds.length ? (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {i.documentIds.map((id) => {
                      const doc = fileById.get(id);
                      if (!doc?.previewUrl) return null;
                      return doc.mimeType.startsWith('image/') ? (
                        <a key={id} href={doc.previewUrl} target="_blank" rel="noreferrer">
                          <img src={doc.previewUrl} alt="" className="size-14 rounded border border-line object-cover hover:border-accent" />
                        </a>
                      ) : (
                        <a key={id} href={doc.previewUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-accent hover:underline">
                          <FileText className="size-3.5" />
                          {doc.fileName}
                        </a>
                      );
                    })}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {events.length ? (
          <div className="border-t border-line pt-3">
            <div className="mb-1 text-xs font-medium uppercase tracking-wide text-ink-faint">Записано от системата</div>
            <ul className="space-y-1.5">
              {events.map((e) => (
                <li key={e.id} className="flex flex-wrap items-baseline gap-x-2 text-sm">
                  <span className="font-mono text-xs text-ink-dim">{formatDate(e.at)}</span>
                  <span className="font-medium">{e.title}</span>
                  <Badge tone="neutral">{SOURCE_BG[e.source]}</Badge>
                  {e.amount !== null ? <span className="text-xs">{money(e.amount, e.ccy)}</span> : null}
                  {e.tripId && e.tripNumber ? (
                    <Link to="/trips/$tripId" params={{ tripId: e.tripId }} className="text-xs text-ink-dim hover:text-accent">
                      {e.tripNumber}
                    </Link>
                  ) : null}
                  {e.detail ? <span className="w-full text-xs text-ink-dim">{e.detail}</span> : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </CardBody>
      {editing ? (
        <IncidentDialog driverId={driverId} incident={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      ) : null}
    </Card>
  );
}

function IncidentDialog({ driverId, incident, onClose }: { driverId: string; incident: Incident | null; onClose: () => void }) {
  const save = useSaveIncident(driverId);
  const vehicles = useVehicles();
  const trips = useTrips({ driverId, perPage: 40 });
  const [form, setForm] = useState({
    occurredOn: incident?.occurredOn ?? new Date().toISOString().slice(0, 10),
    kind: (incident?.kind ?? 'accident') as IncidentKind,
    atFault: (incident?.atFault ?? 'unknown') as AtFault,
    vehicleId: incident?.vehicleId ?? '',
    tripId: incident?.tripId ?? '',
    place: incident?.place ?? '',
    description: incident?.description ?? '',
    cost: incident?.cost != null ? String(incident.cost).replace('.', ',') : '',
    ccy: incident?.ccy ?? 'EUR',
    reference: incident?.reference ?? '',
    beforeEmployment: incident?.beforeEmployment ?? false,
    notes: incident?.notes ?? '',
  });
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const valid = form.occurredOn && form.description.trim().length >= 3;

  const submit = async () => {
    setBusy(true);
    try {
      /** Снимките и протоколът отиват като документи на шофьора, после се връзват към записа. */
      const uploaded: string[] = [];
      for (const file of files) {
        const doc = await uploadDocument(file, {
          type: file.type.startsWith('image/') ? 'photo_damage' : 'other',
          driverId,
          notes: `Досие: ${INCIDENT_KIND_BG[form.kind]} ${form.occurredOn}`,
        });
        uploaded.push(doc.id);
      }
      const cost = form.cost.trim() ? Number(form.cost.replace(/\s/g, '').replace(',', '.')) : null;
      await save.mutateAsync({
        ...(incident ? { id: incident.id } : {}),
        body: {
          occurredOn: form.occurredOn,
          kind: form.kind,
          atFault: form.atFault,
          vehicleId: form.vehicleId || null,
          tripId: form.tripId || null,
          place: form.place.trim() || null,
          description: form.description.trim(),
          cost: cost !== null && Number.isFinite(cost) ? cost : null,
          ccy: form.ccy,
          reference: form.reference.trim() || null,
          beforeEmployment: form.beforeEmployment,
          documentIds: [...(incident?.documentIds ?? []), ...uploaded],
          notes: form.notes.trim() || null,
        },
      });
      toast.ok(incident ? 'Записът е поправен' : 'Вписано в досието');
      onClose();
    } catch (error) {
      toast.error('Не е записано', error instanceof Error ? error.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      width="lg"
      title={incident ? 'Поправка в досието' : 'Ново в досието'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отказ
          </Button>
          <Button variant="primary" loading={busy} disabled={!valid} onClick={() => void submit()}>
            Запиши
          </Button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Дата" required>
          <DatePicker withTime={false} value={form.occurredOn} onChange={(e) => setForm({ ...form, occurredOn: e.target.value })} />
        </Field>
        <Field label="Какво">
          <Select
            value={form.kind}
            onChange={(e) => setForm({ ...form, kind: e.target.value as IncidentKind })}
            options={INCIDENT_KINDS.map((k) => ({ value: k, label: INCIDENT_KIND_BG[k] }))}
          />
        </Field>
        <Field label="Вина">
          <Select
            value={form.atFault}
            onChange={(e) => setForm({ ...form, atFault: e.target.value as AtFault })}
            options={AT_FAULT.map((k) => ({ value: k, label: AT_FAULT_BG[k] }))}
          />
        </Field>
        <Field label="Къде">
          <Input value={form.place} onChange={(e) => setForm({ ...form, place: e.target.value })} placeholder="град, път, адрес" />
        </Field>
        <Field label="Описание" required className="sm:col-span-2">
          <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </Field>
        <Field label="Бус">
          <Select
            value={form.vehicleId}
            onChange={(e) => setForm({ ...form, vehicleId: e.target.value })}
            placeholder="— без бус —"
            options={(vehicles.data?.items ?? []).map((v) => ({ value: v.id, label: `${v.fleetNo} · ${v.plate}` }))}
          />
        </Field>
        <Field label="Курс">
          <Select
            value={form.tripId}
            onChange={(e) => setForm({ ...form, tripId: e.target.value })}
            placeholder="— без курс —"
            options={(trips.data?.items ?? []).map((t) => ({
              value: t.id,
              label: `${t.number}${t.plannedStartAt ? ` · ${formatDate(t.plannedStartAt)}` : ''}`,
            }))}
          />
        </Field>
        <Field label="Щета / глоба">
          <div className="flex gap-1.5">
            <Input inputMode="decimal" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} placeholder="сума" />
            <Select
              className="w-24"
              value={form.ccy}
              onChange={(e) => setForm({ ...form, ccy: e.target.value })}
              options={CURRENCIES.map((c) => ({ value: c, label: c }))}
            />
          </div>
        </Field>
        <Field label="№ на щета / акт / протокол">
          <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
        </Field>
        <Field label={<span className="inline-flex items-center gap-1"><Paperclip className="size-3.5" />Снимки, протокол</span>} className="sm:col-span-2">
          <Input type="file" multiple accept="image/*,application/pdf" onChange={(e) => setFiles([...(e.target.files ?? [])])} />
        </Field>
        <Field label="Бележка" className="sm:col-span-2">
          <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </Field>
        <Checkbox
          className="sm:col-span-2"
          label="Случило се е преди да започне работа при нас"
          checked={form.beforeEmployment}
          onChange={(e) => setForm({ ...form, beforeEmployment: e.target.checked })}
        />
        {incident?.documentIds.length ? (
          <p className="flex items-center gap-1 text-xs text-ink-dim sm:col-span-2">
            <Eye className="size-3.5" />
            Вече прикачени: {incident.documentIds.length}. Новите се добавят към тях.
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
